import React, { useState, useEffect } from 'react';
    import { getAuth } from 'firebase/auth'; // Keep this import for the user prop, even if not directly used inside handlePayment
    import { getFirestore, doc, getDoc } from 'firebase/firestore';
    import { app } from './firebaseConfig'; // Ensure this path is correct relative to PaymentPage.js
    import { httpsCallable } from 'firebase/functions';
    import { getFunctions } from 'firebase/functions';

    // Initialize Firestore and Functions instances using your Firebase app
    const db = getFirestore(app);
    // Ensure 'us-central1' matches the region where you deployed your Cloud Functions
    const functions = getFunctions(app, 'us-central1');

    const PaymentPage = ({ user, onPaymentSuccess, onSignOut }) => {
      const [selectedTier, setSelectedTier] = useState(null);
      const [loading, setLoading] = useState(false); // Manages loading state for the button
      const [error, setError] = useState('');     // Displays error messages to the user
      const [userProfile, setUserProfile] = useState(null); // Stores fetched user data from Firestore
      const [postPaymentLoading, setPostPaymentLoading] = useState(false); // New loading state for post-payment

      // useEffect hook to fetch the user's profile from Firestore on component mount
      // or when the 'user' prop changes.
      useEffect(() => {
        const fetchUserProfile = async () => {
          if (user && db) { // Ensure user object and Firestore DB instance are available
            try {
              const docRef = doc(db, 'users', user.uid); // Reference to the user's document
              const docSnap = await getDoc(docRef);      // Fetch the document
              if (docSnap.exists()) {
                setUserProfile(docSnap.data()); // Set user profile data
                // Check if membership has expired
                if (docSnap.data().membershipExpiry && new Date() > docSnap.data().membershipExpiry.toDate()) {
                    setError('Your membership has expired. Please make a payment to continue.');
                }
              } else {
                setError('User profile not found. Please contact support.');
              }
            } catch (err) {
              console.error("Error fetching user profile:", err);
              setError('Failed to load user data. Please try again.');
            }
          }
        };
        fetchUserProfile();
      }, [user, db]); // Dependencies: re-run if 'user' or 'db' changes

      // --- NEW: After PayPal redirects back, check for payment success and refetch profile ---
      useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment') === 'success';
        if (paymentSuccess && user) {
          setPostPaymentLoading(true);
          // Refetch user profile from Firestore
          (async () => {
            try {
              const docRef = doc(db, 'users', user.uid);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists() && docSnap.data().paymentStatus === 'paid') {
                setUserProfile(docSnap.data());
                onPaymentSuccess(); // Redirect to main website
              } else {
                setError('Payment not confirmed. Please try again or contact support.');
              }
            } catch (err) {
              setError('Failed to verify payment. Please try again.');
            } finally {
              setPostPaymentLoading(false);
            }
          })();
        }
      }, [user, onPaymentSuccess]);

      // Define the available payment tiers.
      // These should match the tiers defined in your Cloud Function for consistency.
      const paymentTiers = [
        { id: 'local', name: 'Local (Botswana Citizens)', price: 50, currency: 'P', description: 'Access for Botswana citizens' },
        { id: 'sadc', name: 'SADC Citizens', price: 100, currency: 'P', description: 'Access for SADC region citizens' },
        { id: 'global', name: 'Global Citizens', price: 380, currency: 'P', description: 'Access for international users' },
      ];

      // Function to handle the payment initiation when the button is clicked.
      const handlePayment = async () => {
        if (!selectedTier) {
          setError('Please select a payment tier.');
          return;
        }
        if (!user) {
            setError('You must be logged in to make a payment.');
            return;
        }

        setLoading(true); // Activate loading state for the button
        setError('');     // Clear any previous error messages

        try {
          // Create a callable function instance that points to your 'createPayPalOrder' Cloud Function.
          const createOrderCallable = httpsCallable(functions, 'createPayPalOrder');

          // Call the Cloud Function, passing the selected tier ID and user ID.
          const response = await createOrderCallable({
            userId: user.uid,
            selectedTierId: selectedTier,
          });

          // Extract the redirect URL and order ID from the Cloud Function's response.
          const { redirectUrl, orderId } = response.data;

          if (redirectUrl) {
            console.log('PayPal Order created:', orderId, 'Redirecting to:', redirectUrl);
            // Redirect the user to PayPal to complete the payment.
            window.location.href = redirectUrl;
          } else {
            // Handle cases where the Cloud Function didn't return a redirect URL.
            setError(response.data?.message || 'Failed to initiate PayPal payment. No redirect URL received from server.');
            console.error("PayPal Initiation Error: No redirectUrl in response", response.data);
          }

        } catch (err) {
          // Catch and log any errors that occur during the callable function invocation.
          console.error("Error during payment initiation:", err.code, err.message);
          // Display a user-friendly error message.
          setError(`Payment initiation failed: ${err.message || 'An unknown error occurred.'}`);
        } finally {
          setLoading(false); // Deactivate loading state regardless of success or failure.
        }
      };

      // Show loading spinner after payment while waiting for Firestore update
      if (postPaymentLoading) {
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-100 font-sans">
            <p className="text-xl text-gray-700">Verifying payment, please wait...</p>
          </div>
        );
      }

      // Conditional rendering: If the user's membership is active, show a welcome message.
      if (userProfile && userProfile.paymentStatus === 'paid' && userProfile.membershipExpiry && new Date() < userProfile.membershipExpiry.toDate()) {
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center rounded-lg">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Welcome!</h2>
              <p className="text-green-600 mb-6">Your membership is active!</p>
              <p className="text-gray-600 mb-6">You can now access the content.</p>
              <button
                onClick={onSignOut}
                className="w-full py-2 px-4 bg-red-600 text-white font-semibold rounded-md shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
              >
                Sign Out
              </button>
            </div>
          </div>
        );
      }

      // Main render: Payment page UI (tier selection and payment button).
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
          <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md rounded-lg">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Choose Your Membership Tier</h2>

            {/* Section to display payment tiers */}
            <div className="space-y-4 mb-6">
              {paymentTiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`p-4 border rounded-md cursor-pointer transition duration-200 ease-in-out
                              ${selectedTier === tier.id ? 'border-blue-600 ring-2 ring-blue-500 shadow-md' : 'border-gray-300 hover:border-blue-400'}`}
                  onClick={() => setSelectedTier(tier.id)}
                >
                  <h3 className="text-xl font-semibold text-gray-800">{tier.name}</h3>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{tier.currency}{tier.price}</p>
                  <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                </div>
              ))}
            </div>

            {/* Error message display */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            {/* Proceed to Pay button */}
            <button
              onClick={handlePayment}
              className="w-full py-3 px-4 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
              disabled={loading || !selectedTier} // Disable if loading or no tier selected
            >
              {loading ? 'Redirecting to PayPal...' : 'Proceed to Pay'}
            </button>

            <p className="text-center text-gray-500 text-sm mt-4">
              Minimum subscription period: 2 weeks. Automatically charged weekly.
            </p>

            {/* Sign Out button */}
            <button
              onClick={onSignOut}
              className="mt-6 w-full py-2 px-4 bg-gray-300 text-gray-800 font-semibold rounded-md shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
            >
              Sign Out
            </button>
          </div>
        </div>
      );
    };

    export default PaymentPage;